<template>
  <div class="mba-panel">
    <div class="mba-header">
      <h2>八字 · 日主 × 月令 審定資料</h2>
      <p class="hint">
        點任一格可編輯。已填的格子背景變綠。共
        {{ filledCount }} / 120 格已寫。
      </p>
      <button class="refresh-btn" @click="fetchAll" :disabled="loading">
        {{ loading ? "讀取中…" : "重新整理" }}
      </button>
    </div>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <div class="legend">
      <span class="dot filled"></span>已填
      <span class="dot empty"></span>未填
    </div>

    <table class="matrix">
      <thead>
        <tr>
          <th></th>
          <th v-for="ds in DAY_STEMS" :key="ds">{{ ds }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="mb in MONTH_BRANCHES" :key="mb">
          <th>{{ mb }}月</th>
          <td
            v-for="ds in DAY_STEMS"
            :key="ds"
            :class="[
              'cell',
              cellOf(ds, mb).filled ? 'filled' : 'empty',
              editingDs === ds && editingMb === mb ? 'active' : '',
            ]"
            @click="openCell(ds, mb)"
          >
            {{ cellOf(ds, mb).filled ? '●' : '·' }}
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 編輯區 -->
    <div v-if="editingDs" class="editor">
      <div class="editor-head">
        <h3>{{ editingDs }} 日生於 {{ editingMb }} 月</h3>
        <button class="close-btn" @click="closeCell">×</button>
      </div>

      <label>
        內容（送 AI 用）
        <span class="cnt">{{ editingContent.length }} 字</span>
      </label>
      <textarea v-model="editingContent" rows="10"></textarea>

      <label>內部備註（不送 AI）</label>
      <textarea v-model="editingNotes" rows="3"></textarea>

      <div class="actions">
        <button class="primary" @click="save" :disabled="saving">
          {{ saving ? "儲存中…" : "儲存" }}
        </button>
        <button class="secondary" @click="closeCell">取消</button>
      </div>

      <p v-if="saveMsg" :class="['msg', saveMsgKind]">{{ saveMsg }}</p>
    </div>
  </div>
</template>

<script>
const DAY_STEMS = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
const MONTH_BRANCHES = [
  "子",
  "丑",
  "寅",
  "卯",
  "辰",
  "巳",
  "午",
  "未",
  "申",
  "酉",
  "戌",
  "亥",
];

export default {
  name: "MiniBaziAdvicePanel",
  props: {
    apiBaseUrl: { type: String, required: true },
    adminToken: { type: String, required: true },
  },
  data() {
    return {
      DAY_STEMS,
      MONTH_BRANCHES,
      items: [], // [{ day_stem, month_branch, content, notes, content_len, ... }]
      loading: false,
      error: "",

      editingDs: "",
      editingMb: "",
      editingContent: "",
      editingNotes: "",

      saving: false,
      saveMsg: "",
      saveMsgKind: "", // 'ok' | 'err'
    };
  },
  computed: {
    filledCount() {
      return this.items.filter((r) => Number(r.content_len) > 0).length;
    },
  },
  async mounted() {
    await this.fetchAll();
  },
  methods: {
    cellOf(ds, mb) {
      const row = this.items.find(
        (r) => r.day_stem === ds && r.month_branch === mb,
      );
      return { filled: row && Number(row.content_len) > 0 };
    },
    async fetchAll() {
      this.loading = true;
      this.error = "";
      try {
        const url = `${this.apiBaseUrl}/api/admin/minibazi-advice`;
        const res = await fetch(url, {
          headers: { "x-admin-token": this.adminToken },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.items = (data && data.items) || [];
      } catch (err) {
        this.error = `讀取失敗：${err.message || err}`;
      } finally {
        this.loading = false;
      }
    },
    async openCell(ds, mb) {
      this.editingDs = ds;
      this.editingMb = mb;
      this.editingContent = "";
      this.editingNotes = "";
      this.saveMsg = "";
      this.saveMsgKind = "";
      try {
        const url =
          `${this.apiBaseUrl}/api/admin/minibazi-advice/cell` +
          `?dayStem=${encodeURIComponent(ds)}&monthBranch=${encodeURIComponent(mb)}`;
        const res = await fetch(url, {
          headers: { "x-admin-token": this.adminToken },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const row = await res.json();
        this.editingContent = (row && row.content) || "";
        this.editingNotes = (row && row.notes) || "";
      } catch (err) {
        this.saveMsg = `讀取失敗：${err.message || err}`;
        this.saveMsgKind = "err";
      }
    },
    closeCell() {
      this.editingDs = "";
      this.editingMb = "";
      this.editingContent = "";
      this.editingNotes = "";
      this.saveMsg = "";
      this.saveMsgKind = "";
    },
    async save() {
      this.saving = true;
      this.saveMsg = "";
      this.saveMsgKind = "";
      try {
        const res = await fetch(
          `${this.apiBaseUrl}/api/admin/minibazi-advice`,
          {
            method: "PUT",
            headers: {
              "x-admin-token": this.adminToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              dayStem: this.editingDs,
              monthBranch: this.editingMb,
              content: this.editingContent,
              notes: this.editingNotes,
            }),
          },
        );
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${text ? " " + text : ""}`);
        }
        this.saveMsg = "✅ 已儲存";
        this.saveMsgKind = "ok";
        await this.fetchAll();
      } catch (err) {
        this.saveMsg = `❌ ${err.message || err}`;
        this.saveMsgKind = "err";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.mba-panel {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
}
.mba-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.mba-header h2 {
  margin: 0;
  font-size: 18px;
}
.hint {
  color: #666;
  font-size: 13px;
  margin: 0;
}
.refresh-btn {
  margin-left: auto;
  padding: 6px 12px;
  font-size: 13px;
  background: #eee;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}
.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.error-banner {
  margin: 8px 0;
  padding: 8px 12px;
  background: #ffe9e9;
  border: 1px solid #f5b5b5;
  color: #a33;
  border-radius: 4px;
  font-size: 13px;
}
.legend {
  margin: 8px 0 16px;
  font-size: 13px;
  text-align: center;
}
.legend .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  vertical-align: middle;
  margin-right: 4px;
  margin-left: 8px;
}
.legend .dot.filled {
  background: #2c8a2c;
}
.legend .dot.empty {
  background: #ccc;
}

.matrix {
  border-collapse: collapse;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
}
.matrix th,
.matrix td {
  padding: 12px 18px;
  border: 1px solid #e5e5e5;
  text-align: center;
  min-width: 52px;
  font-size: 16px;
}
.matrix thead th {
  background: #f5f1e8;
  font-weight: 600;
  color: #6b4f2a;
}
.matrix tbody th {
  background: #fafafa;
  font-weight: 500;
  color: #555;
  text-align: right;
  padding-right: 14px;
}
.cell {
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, transform 0.05s;
  font-size: 18px;
}
.cell.empty {
  color: #d4d4d4;
  background: #ffffff;
}
.cell.filled {
  color: #2c8a2c;
  background: #e8f5e8;
  font-weight: bold;
}
.cell:hover {
  background: #fff7d6;
  transform: scale(1.05);
}
.cell.active {
  outline: 2px solid #3a7;
  outline-offset: -2px;
  background: #fffbe6;
}

.editor {
  margin: 28px auto 0;
  padding: 20px 24px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  max-width: 720px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.editor-head h3 {
  margin: 0;
  font-size: 16px;
}
.close-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
}
.close-btn:hover {
  color: #333;
}
.editor label {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
}
.editor label .cnt {
  font-weight: normal;
  color: #999;
  margin-left: 8px;
  font-size: 12px;
}
.editor textarea {
  width: 100%;
  font-family: inherit;
  font-size: 14px;
  padding: 8px;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  box-sizing: border-box;
}
.editor .actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.editor .actions button {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid;
  cursor: pointer;
}
.editor .actions .primary {
  background: #3a7;
  color: #fff;
  border-color: #2c8a2c;
}
.editor .actions .primary:disabled {
  background: #aaa;
  cursor: not-allowed;
}
.editor .actions .secondary {
  background: #fff;
  color: #555;
  border-color: #ccc;
}
.msg {
  margin-top: 8px;
  font-size: 13px;
}
.msg.ok {
  color: #2c8a2c;
}
.msg.err {
  color: #c33;
}
</style>
